import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Typography,
  Tag,
  Descriptions,
  Divider,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CalculatorOutlined,
  PlusOutlined,
  FileExcelOutlined,
  ExportOutlined,
  SafetyCertificateOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import * as vatReturnService from '@/services/vatReturn.service';
import type { VatReturnRow } from '@/services/vatReturn.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import PostVatReturnModal from '@/components/finance/vat-return/PostVatReturnModal';
import VatSettlementPanel from '@/components/finance/vat-return/VatSettlementPanel';

const { Text } = Typography;

const statusColor: Record<string, string> = {
  draft: 'default',
  calculated: 'processing',
  ready_to_post: 'success',
  validation_issues: 'warning',
  posted: 'green',
  cancelled: 'red',
};

export const VatReturnPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id: detailId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [rows, setRows] = useState<VatReturnRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [detail, setDetail] = useState<VatReturnRow | null>(null);
  const [postingId, setPostingId] = useState<string | null>(null);
  const closePostModal = useCallback(() => setPostingId(null), []);
  const [form] = Form.useForm();

  const statusLabel = useCallback(
    (s: string) => {
      const map: Record<string, string> = {
        draft: t('statusDraftLower'),
        calculated: t('statusCalculated'),
        ready_to_post: t('statusReadyToPost'),
        validation_issues: t('statusValidationIssues'),
        posted: t('statusPostedLower'),
        cancelled: t('statusCancelledLower'),
      };
      return map[s] || s;
    },
    [t]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await vatReturnService.listVatReturns();
      setRows(Array.isArray(list) ? list : []);
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('errLoadVatReturns'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!detailId) return;
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      setDetail(null);
      try {
        const full = await vatReturnService.getVatReturn(detailId);
        if (!cancelled) setDetail(full);
      } catch (e: any) {
        if (!cancelled) {
          message.error(e?.response?.data?.message || t('errLoadFailed'));
          navigate('/fin/tax/vat-return', { replace: true });
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailId, navigate, t]);

  useEffect(() => {
    if (detailId) return;
    load();
  }, [detailId, load]);

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: ColumnsType<VatReturnRow> = [
    { title: t('returnNo'), dataIndex: 'return_number', key: 'return_number' },
    {
      title: t('period'),
      key: 'p',
      render: (_, r) => (
        <span>
          {t('periodTo', {
            from: dayjs(r.period_from).format('YYYY-MM-DD'),
            to: dayjs(r.period_to).format('YYYY-MM-DD'),
          })}
        </span>
      ),
    },
    {
      title: tc('status'),
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag color={statusColor[s] || 'default'}>{statusLabel(s)}</Tag>,
    },
    {
      title: t('outputInputNetAed'),
      key: 'amt',
      render: (_, r) => (
        <Text type="secondary">
          {Number(r.output_vat_total ?? 0).toFixed(2)} / {Number(r.input_vat_total ?? 0).toFixed(2)} /{' '}
          {Number(r.net_vat_due ?? 0).toFixed(2)}
        </Text>
      ),
    },
    {
      title: tc('actions'),
      key: 'a',
      render: (_, r) => (
        <Button type="link" onClick={() => navigate(`/fin/tax/vat-return/${r.id}`)}>
          {t('open')}
        </Button>
      ),
    },
  ];

  if (detailId) {
    return (
      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
        <SmartBreadcrumbs />
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/tax/vat-return')}>
                {t('backToList')}
              </Button>
              {detail && (
                <Space>
                  <SafetyCertificateOutlined />
                  <Text strong>{t('vatReturnWithNumber', { number: detail.return_number })}</Text>
                  <Tag color={statusColor[detail.status] || 'default'}>{statusLabel(detail.status)}</Tag>
                </Space>
              )}
            </Space>
            <Spin spinning={detailLoading}>
              {detail && (
                <>
                  <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 2 }}>
                    <Descriptions.Item label={t('period')}>
                      {t('periodTo', {
                        from: dayjs(detail.period_from).format('YYYY-MM-DD'),
                        to: dayjs(detail.period_to).format('YYYY-MM-DD'),
                      })}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('outputVatAed')}>{Number(detail.output_vat_total).toFixed(2)}</Descriptions.Item>
                    <Descriptions.Item label={t('inputVatAed')}>{Number(detail.input_vat_total).toFixed(2)}</Descriptions.Item>
                    <Descriptions.Item label={t('netOutputMinusInput')}>{Number(detail.net_vat_due).toFixed(2)}</Descriptions.Item>
                  </Descriptions>
                  <Divider />
                  <Space wrap>
                    <Button
                      icon={<CalculatorOutlined />}
                      disabled={detail.status === 'posted'}
                      onClick={async () => {
                        try {
                          const u = await vatReturnService.calculateVatReturn(detail.id);
                          setDetail(u);
                          message.success(t('recalculated'));
                          load();
                        } catch (e: any) {
                          message.error(e?.response?.data?.message || t('errCalculateFailed'));
                        }
                      }}
                    >
                      {t('calculate')}
                    </Button>
                    <Button
                      icon={<ExportOutlined />}
                      disabled={detail.status === 'draft'}
                      onClick={async () => {
                        try {
                          const blob = await vatReturnService.fetchVatReturnExcelBlob(detail.id);
                          triggerBlobDownload(blob, `vat-return-${detail.return_number}.xlsx`);
                        } catch (e: any) {
                          message.error(e?.response?.data?.message || t('errExportFailed'));
                        }
                      }}
                    >
                      {t('exportExcel')}
                    </Button>
                    {detail.status !== 'draft' && <ReportDocumentActions reportKey="vat-return" params={{ id: detail.id }} />}
                    <Button
                      icon={<FileExcelOutlined />}
                      onClick={async () => {
                        try {
                          const u = await vatReturnService.validateVatReturn(detail.id);
                          setDetail(u);
                          message.success(t('validationRun'));
                          load();
                        } catch (e: any) {
                          message.error(e?.response?.data?.message || t('errValidateFailed'));
                        }
                      }}
                    >
                      {t('validate')}
                    </Button>
                    <Button
                      type="primary"
                      disabled={detail.status === 'posted' || detail.status === 'draft'}
                      onClick={() => setPostingId(detail.id)}
                    >
                      {tc('post')}
                    </Button>
                  </Space>
                  {detail.status === 'posted' && (
                    <>
                      <Divider />
                      <VatSettlementPanel returnId={detail.id} refreshKey={detail.posted_at} />
                    </>
                  )}
                  <Divider />
                  <Text strong>{t('vat201StyleLines')}</Text>
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={detail.lines || []}
                    columns={[
                      { title: t('box'), dataIndex: 'box_code' },
                      { title: t('labelCol'), dataIndex: 'label' },
                      {
                        title: t('amountAed'),
                        dataIndex: 'amount_aed',
                        render: (v: number) => Number(v).toFixed(2),
                      },
                    ]}
                  />
                  <Divider />
                  <Text strong>{t('reconciliationSourceDocs')}</Text>
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={{ defaultPageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                    scroll={{ x: true }}
                    dataSource={detail.sourceLinks || []}
                    columns={[
                      { title: t('side'), dataIndex: 'side', width: 90 },
                      { title: t('source'), dataIndex: 'source_type' },
                      { title: t('documentId'), dataIndex: 'source_id', ellipsis: true },
                      {
                        title: t('vatAed'),
                        dataIndex: 'amount_aed',
                        render: (v: number) => Number(v).toFixed(2),
                      },
                      {
                        title: t('ref'),
                        key: 'ref',
                        render: (_: unknown, row: NonNullable<VatReturnRow['sourceLinks']>[0]) =>
                          (row.meta_json?.invoice_number as string) || '—',
                      },
                    ]}
                  />
                  {(detail.validation_warnings_json?.length || detail.validation_errors_json?.length) ? (
                    <>
                      <Divider />
                      {detail.validation_warnings_json?.length ? (
                        <div>
                          <Text type="warning">{t('warnings')}</Text>
                          <ul>
                            {detail.validation_warnings_json.map((w, i) => (
                              <li key={i}>{w}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {detail.validation_errors_json?.length ? (
                        <div>
                          <Text type="danger">{t('errors')}</Text>
                          <ul>
                            {(detail.validation_errors_json as string[]).map((w, i) => (
                              <li key={i}>{w}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </>
              )}
            </Spin>
          </Space>
        </Card>
        <PostVatReturnModal
          returnId={postingId}
          onClose={closePostModal}
          onPosted={(row) => {
            setPostingId(null);
            setDetail(row);
            load();
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <SmartBreadcrumbs />
      <Card
        title={
          <Space direction="vertical" size={0}>
            <Space>
              <SafetyCertificateOutlined />
              <span>{t('uaeVatReturn')}</span>
            </Space>
            <Text type="secondary">{t('uaeVatReturnSubtitle')}</Text>
            <Text type="secondary">{t('statutoryVatWordingNote')}</Text>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            {t('newVatDraft')}
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>

      <Modal
        title={t('newVatReturnDraft')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        cancelText={tc('cancel')}
        okText={tc('save')}
        onOk={async () => {
          try {
            const v = await form.validateFields();
            const period_from = v.period[0].format('YYYY-MM-DD');
            const period_to = v.period[1].format('YYYY-MM-DD');
            const created = await vatReturnService.createVatReturnDraft({
              period_from,
              period_to,
              trn_snapshot: v.trn_snapshot,
              notes: v.notes,
            });
            message.success(t('draftCreated'));
            setModalOpen(false);
            form.resetFields();
            load();
            navigate(`/fin/tax/vat-return/${created.id}`);
          } catch (e: any) {
            if (e?.errorFields) return;
            message.error(e?.response?.data?.message || t('errFailed'));
          }
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="period"
            label={t('taxPeriod')}
            rules={[{ required: true, message: t('selectPeriod') }]}
          >
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="trn_snapshot" label={t('trnSnapshot')}>
            <Input placeholder={tc('optional')} />
          </Form.Item>
          <Form.Item name="notes" label={t('notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VatReturnPage;
